import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadHoursPerWeekDetails,
    loadPlacementEmploymentTypeDetails,
    loadVacancyStageDetails
} from '../../../../store/directusService';
import {
    loadBusinessRecords,
    loadPlacementsForParticipant,
    loadVacancySubmissionsByParticipantId,
    searchVacanciesByServiceId
} from '../../../../store/recruitmentService';
import { searchUsersForVacancies } from '../../../../store/userService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import DynamicSearch from '../../../search/DynamicSearch';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import AddPlacementEditor from '../placements/placement/AddPlacementEditor';

import JobOffersTableRow from './JobOffersTableRow';

import app from '../../../../app.module.css';
import classes from '../submissionStyles/joboffer.module.css';

const headCells = [
    { id: 'code', numeric: false, label: 'ID' },
    { id: 'title', numeric: false, label: 'Title' },
    { id: 'adviser', numeric: false, label: 'Owner' },
    { id: 'jobSectorName', numeric: false, label: 'Job Sector' },
    { id: 'payRate', numeric: false, label: 'Pay Rate' },
    { id: 'postcodeOrRemote', numeric: false, label: 'Location' },
    { id: 'createPlacement', numeric: false, label: 'Create Placement' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'code',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const participantClosedStatusRoles = [MANAGER, QUALITY, SUPERUSER];

const JobOffersTableManagement = ({ onPlacementCreated }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [jobOfferedParticipantVacancies, setJobOfferedParticipantVacancies] = useState([]);
    const [jobOfferedVacancies, setJobOfferedVacancies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpenPlacementEditor, setIsOpenPlacementEditor] = useState(false);
    const [jobPlacementsLoaded, setJobPlacementsLoaded] = useState(false);
    const [businessesForJobsOffered, setBusinessesForJobsOffered] = useState([]);
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const usersForVacancies = useSelector((state) => state.entities.userService.usersForVacancies);
    const hoursPerWeekDetails = useSelector(
        (state) => state.entities.directusService.hoursPerWeekDetails
    );
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const placementEmploymentTypeDetails = useSelector(
        (state) => state.entities.directusService.placementEmploymentTypeDetails
    );
    const vacancyStageDetails = useSelector(
        (state) => state.entities.directusService.vacancyStageDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const businesses = useSelector((state) => state.entities.recruitmentService.businesses);
    const businessesMetaData = useSelector(
        (state) => state.entities.recruitmentService.businessesMetaData
    );
    const placements = useSelector((state) => state.entities.recruitmentService.placements);
    const vacanciesForServices = useSelector(
        (state) => state.entities.recruitmentService.vacanciesForServices
    );
    const vacanciesForServicesMetaData = useSelector(
        (state) => state.entities.recruitmentService.vacanciesForServicesMetaData
    );
    const vacancySubmissions = useSelector(
        (state) => state.entities.recruitmentService.vacancySubmissions
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadPlacementsForParticipant(currentParticipant.id));
        dispatch(loadVacancySubmissionsByParticipantId(currentParticipant.id));
        businesses?.length < 1
            ? dispatch(loadBusinessRecords(0))
            : setBusinessesForJobsOffered(businesses);
        hoursPerWeekDetails?.length < 1 && dispatch(loadHoursPerWeekDetails());
        placementEmploymentTypeDetails?.length < 1 &&
            dispatch(loadPlacementEmploymentTypeDetails());
        vacanciesForServices?.length < 1 &&
            dispatch(
                searchVacanciesByServiceId(
                    { inactive: false, serviceIds: loggedInUser.serviceIds },
                    0
                )
            );
        vacancyStageDetails?.length < 1 && dispatch(loadVacancyStageDetails());
    }, []);

    useEffect(() => {
        if (!loggedInUser || Object.keys(loggedInUser).length < 1) return;
        usersForVacancies?.length < 1 &&
            loggedInUser?.serviceIds?.length > 0 &&
            dispatch(searchUsersForVacancies({ serviceIds: loggedInUser.serviceIds }));
    }, [loggedInUser.id]);

    useEffect(() => {
        if (businessesForJobsOffered?.length < 1) return;

        if (!businessesMetaData.last) {
            dispatch(loadBusinessRecords(businessesMetaData.number + 1));
        } else setBusinessesForJobsOffered(businesses);
    }, [businesses, businessesMetaData]);

    useEffect(() => {
        if (
            vacanciesForServices.length > 0 &&
            'number' in vacanciesForServicesMetaData &&
            !vacanciesForServicesMetaData.last
        )
            dispatch(
                searchVacanciesByServiceId(
                    { inactive: false, serviceIds: loggedInUser.serviceIds },
                    vacanciesForServicesMetaData.number + 1
                )
            );
    }, [vacanciesForServices, vacanciesForServicesMetaData]);

    useEffect(() => {
        if (
            vacanciesForServices?.length < 1 ||
            !usersForVacancies ||
            usersForVacancies?.length < 1 ||
            !jobPlacementsLoaded
        ) {
            setRows([]);
            setJobOfferedParticipantVacancies([]);
            setJobOfferedVacancies([]);
            return;
        }

        const activeOwnerVacancies = vacanciesForServices.map((el) => ({
            ...el,
            businessName:
                businessesForJobsOffered?.find((entry) => entry.id === el.businessRecordId)?.name ||
                '',
            adviser:
                usersForVacancies.find((entry) => entry.id === el?.ownerId)?.firstName +
                ' ' +
                usersForVacancies.find((entry) => entry.id === el?.ownerId)?.lastName,
            placementFound: placements?.some((entry) => entry.vacancyId === el.id),
            jobSectorName: jobSectorDetails.find((entry) => entry.id === el.sectorId)?.name || '',
            payRate: `£${el.payRateFrom} - £${el.payRateTo}`,
            postcodeOrRemote: el.remote ? 'Remote' : el.postcode
        }));
        setJobOfferedParticipantVacancies(activeOwnerVacancies);
    }, [vacanciesForServices, usersForVacancies, jobPlacementsLoaded]);

    useEffect(() => {
        if (jobOfferedParticipantVacancies.length < 1 || vacancySubmissions.length < 1) {
            return;
        }
        const jobOfferedStatusId =
            vacancyStageDetails.find((el) => el.name.toLowerCase() === 'job offered')?.id || '';

        const JobOfferedVacancy = jobOfferedParticipantVacancies
            .map((el) => {
                const foundVacancySubmission = vacancySubmissions.find(
                    (entry) =>
                        entry.participantId === currentParticipant.id &&
                        entry.vacancyId === el.id &&
                        entry.statusId === jobOfferedStatusId
                );
                if (foundVacancySubmission) {
                    return {
                        ...el,
                        vacancySubmissionStatusId: jobOfferedStatusId
                    };
                }
            })
            .filter((el) => el);

        if (JobOfferedVacancy.length > 0) {
            setRows([...rows, ...JobOfferedVacancy]);
            setJobOfferedVacancies([...jobOfferedVacancies, ...JobOfferedVacancy]);
        }
    }, [jobOfferedParticipantVacancies, vacancySubmissions]);

    useEffect(() => {
        if (successMessage === `Job placements have been loaded`) {
            setJobPlacementsLoaded(true);
        }
    }, [successMessage]);

    useEffect(() => {
        const filteredRows = !searchTerm
            ? jobOfferedVacancies
            : jobOfferedVacancies.filter(
                  (el) =>
                      el.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      el.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      el.adviser.toLowerCase().includes(searchTerm.toLowerCase())
              );
        setRows(filteredRows);
    }, [searchTerm]);

    // HELPER FNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <JobOffersTableRow
                key={el.id}
                row={el}
                roles={loggedInUser.roles}
                acceptedRoles={acceptedRoles}
                toggleDropdown={onToggleDropdown}
                openId={openId}
                onPlacementEditor={onPlacementEditorToggle}
                disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
            />
        ));

    // EVENT HANDLERS
    const onToggleDropdown = (id) => setOpenId(id);

    const onSearch = (searchRes) => setSearchTerm(searchRes);

    const onPlacementEditorToggle = (isOpen) => setIsOpenPlacementEditor(isOpen);

    // AWAITING CONTENT
    let content = '';
    if (businesses?.length < 1) content = 'No businesses found';
    if (hoursPerWeekDetails?.length < 1) content = 'No hours per week details';
    if (placementEmploymentTypeDetails?.length < 1)
        content = 'No placement employment type details';
    if (vacanciesForServices?.length < 1) content = 'No vacancy for service ids';
    if (vacancyStageDetails?.length < 1) content = 'No vacancy stage details';
    if (vacancySubmissions?.length < 1) content = 'No vacancy submissions';

    if (
        businesses?.length < 1 ||
        hoursPerWeekDetails?.length < 1 ||
        placementEmploymentTypeDetails?.length < 1 ||
        vacanciesForServices?.length < 1 ||
        vacancyStageDetails?.length < 1 ||
        vacancySubmissions?.length < 1 ||
        !jobPlacementsLoaded
    )
        return <LoadingSpinner content={content} />;

    // RENDER
    return (
        <>
            <h2 className={app.sectionHeading}>Job Offers</h2>
            <div className={classes.jobOfferToolBar}>
                <DynamicSearch
                    disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                    search={onSearch}
                    searchTerm={searchTerm}
                    placeholder="Enter Search Term"
                />
            </div>
            <AddPlacementEditor
                isOpen={isOpenPlacementEditor}
                roles={
                    loggedInUser.roles ||
                    (getNameFromId(participantStatusDetails, currentParticipant.statusId) ===
                        'Closed' &&
                        !hasRole(participantClosedStatusRoles, loggedInUser.roles))
                }
                acceptedRoles={acceptedRoles}
                onPlacementEditorToggle={onPlacementEditorToggle}
                onPlacementCreated={onPlacementCreated}
            />

            {rows.length < 1 ? (
                <LoadingSpinner content="No job offers found" />
            ) : (
                <ResultsTable
                    defaultOrderBy={'code'}
                    sortOrder={rowMetaData.order}
                    headCells={headCells}
                    loadResults={loadBusinessRecords}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}
        </>
    );
};

JobOffersTableManagement.propTypes = {
    onPlacementCreated: PropTypes.func.isRequired
};

export default JobOffersTableManagement;
